import React, { useCallback } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { AppDispatch } from 'app/store';
import { addAlertAction } from 'app/store/Alerts/slice';
import { Button, Flex, Icon, Text } from '@chakra-ui/react';
import { ReactComponent as UploadIcon } from 'resources/images/upload-cloud.svg';
import { uploadImageFromDropzone } from './utils';
import { ERROR_MESSAGES } from './constants';
import { ACCEPTED_FORMATS, MAX_FILE_SIZE } from '../constants';
import { acceptSx, activeSx, baseSx, rejectSx } from './styles';

interface ImageDropzoneProps {
  onImageUploaded: (data: {
    base64: string;
    fileName: string;
    file: File;
    width: number;
    height: number;
  }) => void;
  dispatch: AppDispatch;
  secondaryText?: string;
  minDimension?: number;
}

export const ImageDropzone: React.FC<ImageDropzoneProps> = ({
  onImageUploaded,
  dispatch,
  secondaryText,
  minDimension,
}) => {
  const onDrop = useCallback(
    async (acceptedFiles) => {
      try {
        const response = await uploadImageFromDropzone(
          acceptedFiles,
          minDimension
        );
        onImageUploaded(response);
      } catch (e: any) {
        if (minDimension && e.message === ERROR_MESSAGES.DIMENSION_TOO_SMALL) {
          dispatch(
            addAlertAction({
              message: `Upload a square image with a minimum size of ${minDimension} x ${minDimension} pixels.`,
            })
          );
          return;
        }
        dispatch(
          addAlertAction({ message: 'Unexpected error, please try again' })
        );
      }
    },
    [onImageUploaded, dispatch, minDimension]
  );

  const onDropRejected = (rejections: FileRejection[]) => {
    let errorMessage;
    if (rejections.length > 1) {
      errorMessage = ERROR_MESSAGES.TOO_MANY_FILES;
    } else {
      switch (rejections[0].errors[0].code) {
        case 'file-invalid-type':
          errorMessage = ERROR_MESSAGES.INVALID_FILE_TYPE;
          break;
        case 'file-too-large':
          errorMessage = ERROR_MESSAGES.FILE_TOO_LARGE;
          break;
        default:
          errorMessage = ERROR_MESSAGES.DEFAULT;
      }
    }
    dispatch(addAlertAction({ message: errorMessage }));
  };

  const {
    getRootProps,
    getInputProps,
    open: openFileDialog,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    accept: ACCEPTED_FORMATS,
    onDrop,
    onDropRejected,
  });

  return (
    <Flex
      sx={{
        ...baseSx,
        ...(isDragActive ? activeSx : {}),
        ...(isDragAccept ? acceptSx : {}),
        ...(isDragReject ? rejectSx : {}),
      }}
      {...getRootProps()}
      data-testid="dragAndDrop"
    >
      <input {...getInputProps()} data-testid="image-dropzone" />
      <Icon
        as={UploadIcon}
        flexShrink={0}
        height="96px"
        width="96px"
        color="P.50"
      />
      <Text
        fontSize="lg"
        fontWeight={500}
        color="textBase"
        mt={6}
        textAlign="center"
      >
        Drag and drop an image here
      </Text>
      <Text fontSize="md" color="textSubdued" mt={0.5}>
        or select a file from you file browser.
      </Text>
      {secondaryText && (
        <Text fontSize="md" color="textSubdued" mt={0.5}>
          {secondaryText}
        </Text>
      )}
      <Button mt={6} onClick={openFileDialog} data-testid="browser-button-test">
        Browse
      </Button>
    </Flex>
  );
};
